/**
 * Hook for Impact Assessment dialog state and handlers
 */

import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../services/apiClient";
import {
  fetchAssessmentChecklists,
  fetchAssessmentChecklistById,
  getAssessmentTaskUuid,
  submitBundleTask,
  updateAssessmentChecklist,
  type AssessmentChecklistItem,
  type AssessmentRegulation,
  type AssessmentConcept,
} from "../../services/assessmentService";
import {
  assessmentChecklistToFormValue,
  checklistFormValueToUpdatePayload,
  type ImpactAssessmentChecklistFormValue,
} from "../../components/ImpactAssessmentChecklistForm";
import { useGlobalConfig } from "../../hooks/useGlobalConfig";
import { useConceptCodeMapping } from "../../hooks/useConceptCodeMapping";
import { searchRegulations, visibleProductTypes } from "../../services/regulationsService";
import { transformChecklistToCard, type ChecklistCardItem } from "./constants";
import { getConceptsKeywordsTotal } from "../../components/RegulationsTable/FilterBar/filterBarUtils";
import type { AiResultsModalRow } from "../../components/AssessmentCenterPage";
import {
  aiResultsRowsToAssessmentRegulations,
  markAiResultsRowsForRerun,
  pollByForAiResultsRow,
  pollRegulationAssessmentStatusUntilDone,
} from "./regulationAssessmentPoll";

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error" | "info";
}

export interface UseAutoAssessmentDialogProps {
  open: boolean;
  assessmentSidebarIds: string[];
  /** When set (non-empty), used to load regulation version/market rows for the plan dialog instead of sidebar ids (e.g. landing grid selection). */
  regulationIdsForVersionLookup?: string[];
  runAssessmentIds?: string[];
  onShowSnackbar: (state: SnackbarState) => void;
  onCloseDialog?: () => void;
  regulationVersionOverrides?: Record<string, string>;
  /**
   * Invoked when the AI Impact Assessment Results modal is closed (before state reset).
   * Use to stop Company Scope `regulation-assessment-status` polling so in-flight loops exit.
   */
  onAiResultsModalClose?: () => void;
  /**
   * Cancels an active Company Scope poll before Plan Run / re-run polling starts.
   * Typically the same ref as `onAiResultsModalClose`.
   */
  onCancelExternalResultsPoll?: () => void;
  /** Invoked when assessment polling finishes (refresh list + assessment center). */
  onAssessmentComplete?: () => void;
}

function stripVersionPrefix(version: string | undefined): string | undefined {
  if (!version) return undefined;
  return version.startsWith("v") ? version.substring(1) : version;
}

function formatExecutionDateForGrid(date: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
    .format(date)
    .replace(/\s+/g, "-");
}

export function useAutoAssessmentDialog({
  open,
  assessmentSidebarIds,
  regulationIdsForVersionLookup,
  runAssessmentIds,
  onShowSnackbar,
  onCloseDialog,
  regulationVersionOverrides,
  onAiResultsModalClose,
  onCancelExternalResultsPoll,
  onAssessmentComplete,
}: UseAutoAssessmentDialogProps) {
  const queryClient = useQueryClient();
  const { config: globalConfig } = useGlobalConfig();
  const maxTotalSelections = globalConfig?.max_checklist_concepts ?? 25;
  const maxRegulationsPerBatch = globalConfig?.max_regulations_per_batch ?? 5;

  const [expandedChecklistId, setExpandedChecklistId] = useState<string | null>(
    null,
  );
  const [isEditingChecklist, setIsEditingChecklist] = useState(false);
  const [editingChecklistId, setEditingChecklistId] = useState<string | null>(
    null,
  );
  const [editFormValue, setEditFormValue] =
    useState<ImpactAssessmentChecklistFormValue>({
      name: "",
      description: "",
      selectedConceptIds: [],
      keywords: "",
    });
  const [checklists, setChecklists] = useState<ChecklistCardItem[]>([]);
  const [checklistsLoading, setChecklistsLoading] = useState(false);
  const [checklistsError, setChecklistsError] = useState<string | null>(null);
  const [saveEditChecklistLoading, setSaveEditChecklistLoading] =
    useState(false);
  const [runAssessmentLoading, setRunAssessmentLoading] = useState(false);
  const [regulationVersions, setRegulationVersions] = useState<
    Record<string, string>
  >({});
  const [regulationTitles, setRegulationTitles] = useState<
    Record<string, string>
  >({});
  const [regulationMarkets, setRegulationMarkets] = useState<
    Record<string, string>
  >({});
  const [regulationProductTypes, setRegulationProductTypes] = useState<
    Record<string, string>
  >({});
  const [aiResultsModalOpen, setAiResultsModalOpen] = useState(false);
  const [aiResultsRows, setAiResultsRows] = useState<AiResultsModalRow[]>([]);
  /** Shown on AI Impact Assessment Results DataGrid while status is polled (e.g. Company Scope). */
  const [aiResultsTableLoading, setAiResultsTableLoading] = useState(false);
  const planPollCancelledRef = useRef(false);
  const aiResultsRowsRef = useRef<AiResultsModalRow[]>([]);
  aiResultsRowsRef.current = aiResultsRows;

  const { conceptCodeMapping } = useConceptCodeMapping(open);
  const targetAssessmentIds = runAssessmentIds ?? assessmentSidebarIds;

  const idsForRegulationVersionFetch = useMemo(() => {
    if (regulationIdsForVersionLookup && regulationIdsForVersionLookup.length > 0) {
      return regulationIdsForVersionLookup;
    }
    return assessmentSidebarIds;
  }, [assessmentSidebarIds, regulationIdsForVersionLookup]);

  /** Same rules as Filter Bar edit checklist: name required; 1..max concepts+keywords combined */
  const canSaveEditChecklist = useMemo(() => {
    const nameOk = editFormValue.name.trim() !== "";
    const total = getConceptsKeywordsTotal(editFormValue);
    return nameOk && total >= 1 && total <= maxTotalSelections;
  }, [editFormValue, maxTotalSelections]);

  // Fetch regulation versions when sidebar has ids (for both sidebar and dialog display).
  // On landing page: search API returns latest published version.
  // On detail page: regulationVersionOverrides will override with the viewed version.
  useEffect(() => {
    if (idsForRegulationVersionFetch.length === 0) {
      setRegulationVersions({});
      setRegulationTitles({});
      setRegulationMarkets({});
      setRegulationProductTypes({});
      return;
    }
    searchRegulations({
      filters: {
        status: "3",
        conditions: [
          {
            field: "regulation_id",
            operator: "in",
            value: idsForRegulationVersionFetch,
          },
        ],
      },
      pagination: { page: 1, page_size: 10 },
    })
      .then((res) => {
        if (res.data?.items) {
          const versionMap: Record<string, string> = {};
          const titlesMap: Record<string, string> = {};
          const marketsMap: Record<string, string> = {};
          const productTypesMap: Record<string, string> = {};
          for (const item of res.data.items) {
            const id = item.regulation_id ?? item.id;
            if (!id) continue;
            versionMap[id] = item.version ?? "N/A";
            titlesMap[id] = item.title ?? id;
            const mNames: string[] = [];
            (item.markets ?? []).forEach((m) => {
              if (m.children?.length) {
                m.children.forEach((c) => mNames.push(c.name));
              } else {
                mNames.push(m.name);
              }
            });
            marketsMap[id] =
              [...new Set(mNames.filter(Boolean))].join(", ") || "N/A";
            productTypesMap[id] =
              [
                ...new Set(
                  visibleProductTypes(item.product_types)
                    .map((pt) => pt.name)
                    .filter(Boolean),
                ),
              ].join(", ") || "N/A";
          }
          setRegulationVersions(versionMap);
          setRegulationTitles(titlesMap);
          setRegulationMarkets(marketsMap);
          setRegulationProductTypes(productTypesMap);
        } else {
          setRegulationVersions({});
          setRegulationTitles({});
          setRegulationMarkets({});
          setRegulationProductTypes({});
        }
      })
      .catch(() => {
        setRegulationVersions({});
        setRegulationTitles({});
        setRegulationMarkets({});
        setRegulationProductTypes({});
      });
  }, [idsForRegulationVersionFetch]);

  // Merge regulationVersionOverrides for display: on detail page, use the version
  // the user is viewing; on landing page, use API result (latest published).
  const displayedRegulationVersions = useMemo(
    () => ({
      ...regulationVersions,
      ...(regulationVersionOverrides ?? {}),
    }),
    [regulationVersions, regulationVersionOverrides],
  );

  // Reset edit state and fetch checklists when dialog opens
  useEffect(() => {
    if (!open) return;
    setIsEditingChecklist(false);
    setEditingChecklistId(null);
    setChecklistsLoading(true);
    setChecklistsError(null);
    fetchAssessmentChecklists()
      .then((res) => {
        const items = (res.checklists ?? []).map((item) =>
          transformChecklistToCard(item),
        );
        items.sort(
          (a, b) => (b.raw?.is_favorite ? 1 : 0) - (a.raw?.is_favorite ? 1 : 0),
        );
        setChecklists(items);
      })
      .catch((err) => {
        setChecklistsError(
          err instanceof Error ? err.message : "Failed to load checklists",
        );
        setChecklists([]);
      })
      .finally(() => setChecklistsLoading(false));
  }, [open]);

  // Sync edit form when opening Edit checklist; fetch full checklist by id so Selected Concepts restore
  useEffect(() => {
    if (!isEditingChecklist || !editingChecklistId) return;
    const checklist = checklists.find((c) => c.id === editingChecklistId);
    if (!checklist?.raw) return;
    setEditFormValue(assessmentChecklistToFormValue(checklist.raw));
    fetchAssessmentChecklistById(checklist.raw.id).then((full) => {
      if (full) {
        setEditFormValue(assessmentChecklistToFormValue(full));
      }
    });
  }, [isEditingChecklist, editingChecklistId, checklists]);

  const handleToggleChecklist = useCallback((id: string) => {
    setExpandedChecklistId((prev) => (prev === id ? null : id));
  }, []);

  const handleStartEditChecklist = useCallback((id: string) => {
    setEditingChecklistId(id);
    setIsEditingChecklist(true);
  }, []);

  const handleCancelEditChecklist = useCallback(() => {
    setIsEditingChecklist(false);
    setEditingChecklistId(null);
  }, []);

  const handleSaveEditChecklist = useCallback(async () => {
    if (!editingChecklistId) return;
    const checklist = checklists.find((c) => c.id === editingChecklistId);
    if (!checklist?.raw) {
      onShowSnackbar({
        open: true,
        message: "Checklist not found.",
        severity: "error",
      });
      return;
    }

    const nameTrimmed = editFormValue.name.trim();
    if (!nameTrimmed) {
      onShowSnackbar({
        open: true,
        message: "Please enter a plan name.",
        severity: "error",
      });
      return;
    }

    const conceptsKeywordsTotal = getConceptsKeywordsTotal(editFormValue);
    if (conceptsKeywordsTotal < 1) {
      onShowSnackbar({
        open: true,
        message:
          "Please select at least one Concept or add at least one Keyword.",
        severity: "error",
      });
      return;
    }
    if (conceptsKeywordsTotal > maxTotalSelections) {
      onShowSnackbar({
        open: true,
        message:
          `Total selections across concepts and keywords cannot exceed ${maxTotalSelections}.`,
        severity: "error",
      });
      return;
    }

    const currentChecklistId = checklist.raw.id;
    const isDuplicateEdit = checklists.some(
      (c) =>
        c.raw?.id !== currentChecklistId &&
        c.name.trim().toLowerCase() === nameTrimmed.toLowerCase(),
    );
    if (isDuplicateEdit) {
      onShowSnackbar({
        open: true,
        message: `A checklist with name "${nameTrimmed}" already exists.`,
        severity: "error",
      });
      return;
    }

    setSaveEditChecklistLoading(true);
    try {
      const payload = checklistFormValueToUpdatePayload(
        editFormValue,
        checklist.raw.is_favorite,
        conceptCodeMapping
      );
      const res = await updateAssessmentChecklist(checklist.raw.id, payload);
      const isApiError =
        res.status !== "success" || (res.code !== undefined && res.code >= 400);
      if (isApiError) {
        onShowSnackbar({
          open: true,
          message:
            res.message ??
            "Failed to update checklist. A checklist with this name may already exist.",
          severity: "error",
        });
        return;
      }
      onShowSnackbar({
        open: true,
        message: "Checklist updated successfully.",
        severity: "success",
      });
      handleCancelEditChecklist();
      // Refetch the updated checklist by ID so we get full concepts and correct counts
      const updated = await fetchAssessmentChecklistById(currentChecklistId);
      if (updated) {
        const updatedCard = transformChecklistToCard(updated);
        setChecklists((prev) => {
          const next = prev.map((c) =>
            c.id === String(currentChecklistId) ? updatedCard : c,
          );
          next.sort(
            (a, b) =>
              (b.raw?.is_favorite ? 1 : 0) - (a.raw?.is_favorite ? 1 : 0),
          );
          return next;
        });
      } else {
        const fetchRes = await fetchAssessmentChecklists();
        const items = (fetchRes.checklists ?? []).map((item) =>
          transformChecklistToCard(item),
        );
        items.sort(
          (a, b) => (b.raw?.is_favorite ? 1 : 0) - (a.raw?.is_favorite ? 1 : 0),
        );
        setChecklists(items);
      }
    } catch (error) {
      onShowSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update checklist. Please try again.",
        severity: "error",
      });
    } finally {
      setSaveEditChecklistLoading(false);
    }
  }, [
    editingChecklistId,
    editFormValue,
    checklists,
    conceptCodeMapping,
    handleCancelEditChecklist,
    maxTotalSelections,
    onShowSnackbar,
  ]);

  const cancelPlanRegulationStatusPolling = useCallback(() => {
    planPollCancelledRef.current = true;
    setAiResultsTableLoading(false);
  }, []);

  const handleCloseAiResultsModal = useCallback(() => {
    cancelPlanRegulationStatusPolling();
    onAiResultsModalClose?.();
    setAiResultsModalOpen(false);
    setAiResultsTableLoading(false);
  }, [cancelPlanRegulationStatusPolling, onAiResultsModalClose]);

  const openAiResultsModalWithRows = useCallback((rows: AiResultsModalRow[]) => {
    setAiResultsRows(rows);
    setAiResultsModalOpen(true);
  }, []);

  const updateAiResultsModalRows = useCallback(
    (
      rowsOrUpdater:
        | AiResultsModalRow[]
        | ((previous: AiResultsModalRow[]) => AiResultsModalRow[]),
    ) => {
      setAiResultsRows((prev) =>
        typeof rowsOrUpdater === "function"
          ? rowsOrUpdater(prev)
          : rowsOrUpdater,
      );
    },
    [],
  );

  const buildAiResultsRows = useCallback(
    (
      regulations: AssessmentRegulation[],
      checklist: ChecklistCardItem | AssessmentChecklistItem,
    ): AiResultsModalRow[] => {
      const raw =
        "raw" in checklist && checklist.raw ? checklist.raw : (checklist as AssessmentChecklistItem);
      const impactedConcepts = [
        ...(raw.concepts?.concepts ?? []).flatMap((c) =>
          (c.values ?? []).map((v) => v.name),
        ),
        ...(raw.keywords ?? []),
      ].join(", ");
      const executedDate = formatExecutionDateForGrid(new Date());

      return regulations.map((regulation) => ({
        id: `${regulation.id}:${regulation.version}:running`,
        regulationId: regulation.id,
        regulation: regulation.title || regulation.id,
        version: regulation.version,
        executedDate,
        rawStatus: "running",
        status: "Running",
        executedBy: "Current User",
        assessmentType: "user",
        impactedConcepts: impactedConcepts || "—",
        impactLevel: "Pending",
        reportUrl: null,
        taskUuid: "",
      }));
    },
    [],
  );

  const refreshAssessmentCenter = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["assessment-center"] });
    onAssessmentComplete?.();
  }, [queryClient, onAssessmentComplete]);

  const resumeAiResultsPollAfterRerun = useCallback(
    (row: AiResultsModalRow) => {
      const taskUuid = row.taskUuid?.trim();
      if (!taskUuid) return;

      cancelPlanRegulationStatusPolling();
      onCancelExternalResultsPoll?.();

      const prev = aiResultsRowsRef.current;
      const taskRows = prev.filter((r) => (r.taskUuid ?? "").trim() === taskUuid);
      const regulations = aiResultsRowsToAssessmentRegulations(
        taskRows.length > 0 ? taskRows : [row],
      );
      const by = pollByForAiResultsRow(row.assessmentType);

      updateAiResultsModalRows((rows) => markAiResultsRowsForRerun(rows, row));

      planPollCancelledRef.current = false;
      void pollRegulationAssessmentStatusUntilDone({
        taskUuid,
        regulations,
        by,
        updateAiResultsModalRows,
        setAiResultsTableLoading,
        refreshAssessmentCenter,
        isCancelled: () => planPollCancelledRef.current,
      }).catch(() => refreshAssessmentCenter());
    },
    [
      cancelPlanRegulationStatusPolling,
      onCancelExternalResultsPoll,
      refreshAssessmentCenter,
      updateAiResultsModalRows,
    ],
  );

  const pollPlanRegulationAssessmentStatus = useCallback(
    async (taskUuid: string, regulations: AssessmentRegulation[]) => {
      onCancelExternalResultsPoll?.();
      planPollCancelledRef.current = false;
      await pollRegulationAssessmentStatusUntilDone({
        taskUuid,
        regulations,
        by: "user",
        updateAiResultsModalRows,
        setAiResultsTableLoading,
        refreshAssessmentCenter,
        isCancelled: () => planPollCancelledRef.current,
      });
    },
    [
      onCancelExternalResultsPoll,
      refreshAssessmentCenter,
      updateAiResultsModalRows,
    ],
  );

  const handleRunAssessment: (
    checklist: ChecklistCardItem | AssessmentChecklistItem,
  ) => Promise<void> = useCallback(
    async (checklist) => {
      if (targetAssessmentIds.length === 0) {
        onShowSnackbar({
          open: true,
          message: "Please select at least one regulation to run assessment.",
          severity: "error",
        });
        return;
      }
      if (targetAssessmentIds.length > maxRegulationsPerBatch) {
        onShowSnackbar({
          open: true,
          message: `Please select at most ${maxRegulationsPerBatch} regulations to run assessment.`,
          severity: "error",
        });
        return;
      }

      setRunAssessmentLoading(true);
      try {
        const regulationPromises = targetAssessmentIds.map(async (id) => {
          try {
            const overrideVersion = regulationVersionOverrides?.[id];
            const versionNum = stripVersionPrefix(overrideVersion);
            const url = versionNum
              ? `/regulations/${id}/version/${versionNum}`
              : `/regulations/${id}`;
            const result = await apiClient.get(url);
            if (result.status === "success" && result.data) {
              const docs = result.data.associated_documents || [];
              const documents = docs.map(
                (d: {
                  file_path?: string;
                  path?: string;
                  name?: string;
                  guid?: string;
                  id?: string;
                }) => ({
                  file_path: d.file_path ?? d.path ?? "",
                  name: d.name ?? "",
                  guid: d.guid ?? d.id ?? "",
                }),
              );
              return {
                id: result.data.regulation_id || result.data.id || id,
                version: result.data.version || versionNum || "1.0",
                title: result.data.title || `Regulation ${id}`,
                documents,
              } as AssessmentRegulation;
            }
            return null;
          } catch (error) {
            console.error(`Error fetching regulation ${id}:`, error);
            return null;
          }
        });

        const regulationResults = await Promise.all(regulationPromises);
        const regulations = regulationResults.filter(
          (reg): reg is AssessmentRegulation => reg !== null,
        );

        if (regulations.length === 0) {
          onShowSnackbar({
            open: true,
            message: "Failed to fetch regulation details. Please try again.",
            severity: "error",
          });
          return;
        }

        const rawPlan =
          "raw" in checklist && checklist.raw
            ? checklist.raw
            : (checklist as unknown as AssessmentChecklistItem);

        const conceptsList = rawPlan.concepts?.concepts ?? [];
        const concepts: AssessmentConcept[] = conceptsList.map((c) => ({
          concept: {
            name: c.concept_type || c.concept_code || "Concept",
            code: c.concept_code || c.concept_type || "",
            description: c.concept_type || "",
          },
          values: (c.values ?? []).map((v) => ({
            name: v.name,
            code: v.code ?? v.name,
            description: v.name,
          })),
        }));

        const keywords = rawPlan.keywords ?? [];

        const payload = { regulations, concepts, keywords };

        const response = await submitBundleTask(payload);

        if (response.status === "success" || response.code === 200) {
          const taskUuid = getAssessmentTaskUuid(response);
          const taskUuidStr = taskUuid ?? "";
          setAiResultsRows(
            buildAiResultsRows(regulations, checklist).map((row) => ({
              ...row,
              taskUuid: taskUuidStr,
            })),
          );
          setAiResultsModalOpen(true);
          onCloseDialog?.();
          const planName = "name" in checklist ? checklist.name : rawPlan.name;
          onShowSnackbar({
            open: true,
            message: `Impact assessment started for ${targetAssessmentIds.length} regulation(s) using plan "${planName}". This may take a few minutes.`,
            severity: "success",
          });
          if (taskUuid) {
            void pollPlanRegulationAssessmentStatus(taskUuid, regulations).catch(
              (error) => {
                console.error("Failed to poll assessment task status:", error);
                refreshAssessmentCenter();
              },
            );
          } else {
            refreshAssessmentCenter();
          }
        } else {
          throw new Error(response.message || "Assessment submission failed");
        }
      } catch (error) {
        onShowSnackbar({
          open: true,
          message:
            error instanceof Error
              ? error.message
              : "Failed to run assessment. Please try again.",
          severity: "error",
        });
      } finally {
        setRunAssessmentLoading(false);
      }
    },
    [
      targetAssessmentIds,
      maxRegulationsPerBatch,
      onShowSnackbar,
      onCloseDialog,
      regulationVersionOverrides,
      buildAiResultsRows,
      pollPlanRegulationAssessmentStatus,
      refreshAssessmentCenter,
    ],
  );

  const handleRunAssessmentByPlan = useCallback(
    async (plan: AssessmentChecklistItem) => {
      await handleRunAssessment(plan);
    },
    [handleRunAssessment],
  );

  return {
    expandedChecklistId,
    isEditingChecklist,
    editingChecklistId,
    editFormValue,
    setEditFormValue,
    canSaveEditChecklist,
    checklists,
    setChecklists,
    checklistsLoading,
    checklistsError,
    saveEditChecklistLoading,
    runAssessmentLoading,
    regulationVersions: displayedRegulationVersions,
    regulationTitles,
    regulationMarkets,
    regulationProductTypes,
    aiResultsModalOpen,
    aiResultsRows,
    aiResultsTableLoading,
    setAiResultsTableLoading,
    handleCloseAiResultsModal,
    handleToggleChecklist,
    handleStartEditChecklist,
    handleCancelEditChecklist,
    handleSaveEditChecklist,
    handleRunAssessment,
    handleRunAssessmentByPlan,
    openAiResultsModalWithRows,
    updateAiResultsModalRows,
    resumeAiResultsPollAfterRerun,
  };
}
